import * as i3 from '@angular/cdk/collections';
import { NgIf, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Directive, ContentChildren, forwardRef, Output, Input, Component, Optional } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

class TlkRadioButtonUniqueIdService {
    constructor() {
        this.nextUniqueId = 0;
    }
    getRadioButtonGroupId() {
        return this.nextUniqueId++;
    }
    getRadioButtonId() {
        return ++this.nextUniqueId;
    }
}
TlkRadioButtonUniqueIdService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkRadioButtonUniqueIdService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TlkRadioButtonUniqueIdService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkRadioButtonUniqueIdService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkRadioButtonUniqueIdService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class TlkRadioGroupDirective {
    get selected() {
        return this._selected;
    }
    set selected(selected) {
        this._selected = selected;
        this.value = selected ? selected.value : null;
        this.checkSelectedRadioButton();
    }
    /** Whether the radio group is required */
    get required() {
        return this._required;
    }
    set required(value) {
        this._required = value;
        this._markRadiosForCheck();
    }
    /** Whether the radio group is disabled */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = value;
        this._markRadiosForCheck();
    }
    constructor(_changeDetectorRef, nextUniqueId, renderer, el) {
        this._changeDetectorRef = _changeDetectorRef;
        this.nextUniqueId = nextUniqueId;
        this.renderer = renderer;
        this.el = el;
        /** Selected value for the radio group */
        this.value = null;
        /** The currently selected radio button */
        this._selected = null;
        /** Subscription to changes in the radio buttons */
        this.radioButtonChangeSubscription = null;
        /** radio group is required. */
        this._required = false;
        /** radio group is disabled. */
        this._disabled = false;
        /** Event emitted when the group value changes */
        this._change = new EventEmitter();
        /** Callback registered via ControlValueAccessor */
        this._onChange = () => { };
        this._onTouched = () => { };
    }
    ngOnInit() {
        const uniqueId = this.nextUniqueId.getRadioButtonGroupId();
        if (!this.name) {
            this.name = `tlk-radio-group-${uniqueId}`;
        }
    }
    ngAfterContentInit() {
        this.updateRadioButtonNames();
        this._subscribeToRadioButtonChanges();
        this.applyStyles();
    }
    ngOnDestroy() {
        this.radioButtonChangeSubscription?.unsubscribe();
    }
    /** ControlValueAccessor implementation */
    writeValue(value) {
        this.value = value;
        this.updateSelectedRadioFromValue();
        this._changeDetectorRef.markForCheck();
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    updateRadioButtonNames() {
        this._radios.forEach(radioButton => {
            radioButton.name = this.name;
            radioButton.disabled = this.disabled;
        });
    }
    /** Handle changes in the radio button group */
    _subscribeToRadioButtonChanges() {
        this.radioButtonChangeSubscription = this._radios.changes.subscribe(() => {
            this.updateRadioButtonNames();
        });
        this._radios.forEach(radioButton => {
            radioButton.change.subscribe(() => {
                this.selectRadio(radioButton);
            });
        });
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this._markRadiosForCheck();
    }
    updateSelectedRadioFromValue() {
        if (this._radios) {
            this._radios.forEach(radioButton => {
                radioButton.checked = this.value === radioButton.value;
            });
        }
    }
    selectRadio(selectedRadioButton) {
        this.value = selectedRadioButton.value;
        this._selected = selectedRadioButton;
        this.updateSelectedRadioFromValue();
        this._onChange(this.value);
        this._onTouched(this.value);
        this._change.emit(this.value);
    }
    _markRadiosForCheck() {
        if (this._radios) {
            this._radios.forEach(radioButton => radioButton.markForCheck());
        }
    }
    applyStyles() {
        this.renderer.setStyle(this.el.nativeElement, 'display', 'flex');
        this.renderer.setStyle(this.el.nativeElement, 'flexDirection', 'column');
        this.renderer.setStyle(this.el.nativeElement, 'gap', '24px');
    }
    checkSelectedRadioButton() {
        if (this._selected && !this._selected.checked) {
            this._selected.checked = true;
        }
    }
    // get radio group name
    getGroupName() {
        return this.name ? this.name : null;
    }
    // get value
    getCheckedValue() {
        return this.value !== null && this.value !== undefined ? this.value : null;
    }
}
TlkRadioGroupDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkRadioGroupDirective, deps: [{ token: i0.ChangeDetectorRef }, { token: TlkRadioButtonUniqueIdService }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
TlkRadioGroupDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: TlkRadioGroupDirective, isStandalone: true, selector: "tlk-radio-group", inputs: { name: "name", selected: "selected", required: "required", disabled: "disabled" }, outputs: { _change: "_change" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: TlkRadioGroupDirective,
            multi: true
        }
    ], queries: [{ propertyName: "_radios", predicate: i0.forwardRef(function () { return TlkRadioButtonComponent; }), descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkRadioGroupDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'tlk-radio-group',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: TlkRadioGroupDirective,
                            multi: true
                        }
                    ],
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: TlkRadioButtonUniqueIdService }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { _radios: [{
                type: ContentChildren,
                args: [forwardRef(() => TlkRadioButtonComponent), { descendants: true }]
            }], _change: [{
                type: Output
            }], name: [{
                type: Input
            }], selected: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

class TlkRadioButtonComponent {
    set disabled(value) {
        this._disabled = value;
        this.markForCheck();
    }
    get disabled() {
        return this._disabled || (this.radioGroup && this.radioGroup.disabled);
    }
    get change() {
        return this._change;
    }
    get checked() {
        return this._checked;
    }
    set checked(state) {
        if (this._checked !== state) {
            this._checked = state;
            if (state && this.radioGroup && this.radioGroup.value !== this.value) {
                this.radioGroup.selected = this;
            }
            else if (!state && this.radioGroup && this.radioGroup.value === this.value) {
                this.radioGroup.selected = null;
            }
            if (state) {
                this.radioDispatcher.notify(this.id, this.name);
            }
            this.changeDetector.markForCheck();
        }
    }
    get required() {
        return this._required || (this.radioGroup && this.radioGroup.required);
    }
    set required(value) {
        this._required = value;
    }
    constructor(radioGroup, changeDetector, nextUniqueId, radioDispatcher) {
        this.radioGroup = radioGroup;
        this.changeDetector = changeDetector;
        this.nextUniqueId = nextUniqueId;
        this.radioDispatcher = radioDispatcher;
        this._required = false;
        this._checked = false;
        this._disabled = false;
        this._change = new EventEmitter(); // Event emitter for change
    }
    ngOnInit() {
        const uniqueId = this.nextUniqueId.getRadioButtonId();
        // Set unique ID if not provided
        if (!this.id) {
            this.id = `tlk-radio-${uniqueId}`;
        }
        // If part of a radio group, inherit the group’s name and disabled state
        if (this.radioGroup) {
            this.name = this.radioGroup.name;
            this.disabled = this.radioGroup.disabled;
            this.checked = this.radioGroup.value === this.value;
        }
    }
    onChange(event) {
        const inputElement = event.target;
        this.checked = inputElement.checked;
        this._change.emit(this.value);
        // Notify the group of the change if part of a group
        if (this.radioGroup) {
            this.radioGroup.selectRadio(this);
        }
    }
    /** Marks the component for change detection */
    markForCheck() {
        this.changeDetector.markForCheck();
    }
}
TlkRadioButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkRadioButtonComponent, deps: [{ token: TlkRadioGroupDirective, optional: true }, { token: i0.ChangeDetectorRef }, { token: TlkRadioButtonUniqueIdService }, { token: i3.UniqueSelectionDispatcher }], target: i0.ɵɵFactoryTarget.Component });
TlkRadioButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: TlkRadioButtonComponent, isStandalone: true, selector: "tlk-radio-button", inputs: { id: "id", value: "value", label: "label", description: "description", name: "name", arialabel: "arialabel", arialabelledby: "arialabelledby", disabled: "disabled", checked: "checked", required: "required" }, outputs: { _change: "_change" }, ngImport: i0, template: "<label class=\"tlk-radio-button-container\" [ngClass]=\"{ disabled: disabled }\">\n\t<input\n\t\ttype=\"radio\"\n\t\t[attr.aria-label]=\"arialabel\"\n\t\t[attr.aria-labelledby]=\"arialabelledby\"\n\t\t[id]=\"id\"\n\t\t[name]=\"name\"\n\t\t[disabled]=\"disabled\"\n\t\t[checked]=\"checked\"\n\t\t[value]=\"value\"\n\t\t(change)=\"onChange($event)\"\n\t/>\n\t<div class=\"label-container\">\n\t\t<p class=\"label-text\">{{ label }}</p>\n\t\t<p *ngIf=\"description\" class=\"label-description\">{{ description }}</p>\n\t</div>\n</label>\n", styles: ["input[type=radio]{--radio-border-color: var(--tlk-gray-60);--radio-checked-color: var(--tlk-blue-80);--radio-hover-color: rgba(64, 141, 193, .2);--radio-disabled-bg-color: var(--tlk-white);--radio-focused-color: var(--tlk-blue-60);cursor:pointer;box-sizing:border-box;width:20px;height:20px;margin:4px;padding:0;border:1px solid var(--radio-border-color);border-radius:50%;appearance:none;background-color:transparent;outline:none;flex-shrink:0}input[type=radio]:not(:disabled):checked{border-color:var(--radio-border-color);background-color:var(--radio-checked-color);background-clip:content-box;padding:5.2px;background-image:radial-gradient(circle,var(--radio-checked-color) 0%,var(--radio-checked-color) 50%,transparent 60%,transparent 100%)}input[type=radio]:disabled{background-color:var(--radio-disabled-bg-color);border:1px solid var(--tlk-gray-50);cursor:unset}input[type=radio]:disabled:checked{background-clip:content-box;padding:5.2px;background-image:radial-gradient(circle,var(--radio-border-color) 0%,var(--radio-border-color) 50%,transparent 50%,transparent 100%)}input[type=radio]:focus-visible{outline:2px solid var(--radio-focused-color)}@media (hover: hover){input[type=radio]:not(:disabled):hover{outline:8px solid var(--radio-hover-color)}}.tlk-radio-button-container{display:flex;align-items:flex-start;gap:4px;padding:4px}.tlk-radio-button-container.disabled{color:var(--tlk-gray-50)}.tlk-radio-button-container input[type=radio] .disabled{cursor:default}.tlk-radio-button-container .label-container{display:flex;flex-direction:column;gap:4px}.tlk-radio-button-container .label-container .label-text{margin:0;font:var(--tlk-text-radio-label-text-font)}.tlk-radio-button-container .label-container .label-description{margin:0;font:var(--tlk-text-label-description-font);box-sizing:border-box;white-space:normal;word-wrap:break-word}\n"], dependencies: [{ kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkRadioButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tlk-radio-button', standalone: true, imports: [TlkRadioGroupDirective, NgIf, NgClass], template: "<label class=\"tlk-radio-button-container\" [ngClass]=\"{ disabled: disabled }\">\n\t<input\n\t\ttype=\"radio\"\n\t\t[attr.aria-label]=\"arialabel\"\n\t\t[attr.aria-labelledby]=\"arialabelledby\"\n\t\t[id]=\"id\"\n\t\t[name]=\"name\"\n\t\t[disabled]=\"disabled\"\n\t\t[checked]=\"checked\"\n\t\t[value]=\"value\"\n\t\t(change)=\"onChange($event)\"\n\t/>\n\t<div class=\"label-container\">\n\t\t<p class=\"label-text\">{{ label }}</p>\n\t\t<p *ngIf=\"description\" class=\"label-description\">{{ description }}</p>\n\t</div>\n</label>\n", styles: ["input[type=radio]{--radio-border-color: var(--tlk-gray-60);--radio-checked-color: var(--tlk-blue-80);--radio-hover-color: rgba(64, 141, 193, .2);--radio-disabled-bg-color: var(--tlk-white);--radio-focused-color: var(--tlk-blue-60);cursor:pointer;box-sizing:border-box;width:20px;height:20px;margin:4px;padding:0;border:1px solid var(--radio-border-color);border-radius:50%;appearance:none;background-color:transparent;outline:none;flex-shrink:0}input[type=radio]:not(:disabled):checked{border-color:var(--radio-border-color);background-color:var(--radio-checked-color);background-clip:content-box;padding:5.2px;background-image:radial-gradient(circle,var(--radio-checked-color) 0%,var(--radio-checked-color) 50%,transparent 60%,transparent 100%)}input[type=radio]:disabled{background-color:var(--radio-disabled-bg-color);border:1px solid var(--tlk-gray-50);cursor:unset}input[type=radio]:disabled:checked{background-clip:content-box;padding:5.2px;background-image:radial-gradient(circle,var(--radio-border-color) 0%,var(--radio-border-color) 50%,transparent 50%,transparent 100%)}input[type=radio]:focus-visible{outline:2px solid var(--radio-focused-color)}@media (hover: hover){input[type=radio]:not(:disabled):hover{outline:8px solid var(--radio-hover-color)}}.tlk-radio-button-container{display:flex;align-items:flex-start;gap:4px;padding:4px}.tlk-radio-button-container.disabled{color:var(--tlk-gray-50)}.tlk-radio-button-container input[type=radio] .disabled{cursor:default}.tlk-radio-button-container .label-container{display:flex;flex-direction:column;gap:4px}.tlk-radio-button-container .label-container .label-text{margin:0;font:var(--tlk-text-radio-label-text-font)}.tlk-radio-button-container .label-container .label-description{margin:0;font:var(--tlk-text-label-description-font);box-sizing:border-box;white-space:normal;word-wrap:break-word}\n"] }]
        }], ctorParameters: function () { return [{ type: TlkRadioGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }, { type: TlkRadioButtonUniqueIdService }, { type: i3.UniqueSelectionDispatcher }]; }, propDecorators: { id: [{
                type: Input
            }], value: [{
                type: Input
            }], label: [{
                type: Input
            }], description: [{
                type: Input
            }], name: [{
                type: Input
            }], arialabel: [{
                type: Input
            }], arialabelledby: [{
                type: Input
            }], disabled: [{
                type: Input
            }], _change: [{
                type: Output
            }], checked: [{
                type: Input
            }], required: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { TlkRadioButtonComponent, TlkRadioGroupDirective };
